import { useEffect, useState } from "react";
import { CurrentBrand } from "modules/Types";
import { Body2 } from "components/shareable/Typography";
import useBrand from 'Hooks/useBrand'
import { useHistory } from "react-router-dom"
import { toastError } from "modules/Toast"
import OrganizationTabSection from "../OrganizationTabSection";
import { GridColDef } from "@mui/x-data-grid";
import OrganizationViewDefaultGrid from "../componentsOrganization/OrganizationViewDefaultGrid";
import { format } from 'date-fns';
import LinkContainer from 'components/shareable/LinkContainer'
import { Container } from 'modules/Sty'
import { SYMPHONY_WEBSITE_URL, WEBSITE_TYPE_INFO, WebsiteType } from "modules/Const";
import BrandSelectionModal from "components/shareable/CreateCampaignModal/SelectBrandModal";
import TableEmptyState from "./OrganizationTabEmptyState";
import { getOrganizationWebsites_api } from "api/organizationApi";
import ButtonSymphony from "elements/ButtonSymphony";
import useOrganization from "Hooks/useOrganization";
import OrganizationTabLoading from "../componentsOrganization/OrganizationTabLoading";
import TagSymphony from "components/shareable/TagSymphony";
import useWebsiteBuilder from "Hooks/useWebsiteBuilder";

const PAGE_SIZE = 8;

interface Website {
    id: string;
    name: string;
    type: string;
    creationType: 'automatic' | 'campaign' | 'presave';
    url: string;
    brandName: string;
    brandImage: string;
    createdAt: string;
    brandSlug: string;
    campaign_id?: string;
    slug: string;
    content_metadata?: {
        presave?: {
            saved_at: string | null;
        };
    };
}

interface OrganizationWebsitesTabProps {
    organizationBrands: CurrentBrand[];
}

const OrganizationTabWebsites = ({
    organizationBrands,
}: OrganizationWebsitesTabProps) => {
    const history = useHistory();
    const { setCurrentBrand } = useBrand();
    const { isLoadingTab, setIsLoadingTab, getOrganizationId } = useOrganization();
    const {
        setShowCreateNewWebsiteModal,
        setInitialWebsiteType
    } = useWebsiteBuilder();
    const organizationId = getOrganizationId();

    const [websites, setWebsites] = useState<Website[]>([]);
    const [page, setPage] = useState<number>(0);
    const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
    const [rowCount, setRowCount] = useState<number>(0);

    const fetchWebsites = async () => {
        try {
            setIsLoadingTab(true);
            const response = await getOrganizationWebsites_api(organizationId, page * PAGE_SIZE, PAGE_SIZE);

            if (response?.websites) {
                const websitesWithFullUrls = response.websites.map((website: any) => ({
                    ...website,
                    url: `${SYMPHONY_WEBSITE_URL}${website.url}`
                }));
                setWebsites(websitesWithFullUrls);
                setRowCount(response.total);
            } else {
                throw new Error('Failed to fetch websites');
            }
        } catch (error) {
            toastError('Failed to load websites');
        } finally {
            setIsLoadingTab(false);
        }
    };

    useEffect(() => {
        fetchWebsites();
    }, [page]);

    const handleRowClick = (row: Website) => {
        console.log('row', row);
        const brandForWebsite = organizationBrands.find(
            brand => brand.slug === row.brandSlug
        );
        if (brandForWebsite) {
            setCurrentBrand(brandForWebsite);
            const isUnsavedPresave = row.content_metadata?.presave &&
                !row.content_metadata.presave.saved_at;

            const editPath = isUnsavedPresave
                ? `/marketing/campaigns/${row.campaign_id}`
                : `/website/${row.type}/${row.slug}`;

            history.push(editPath);
        } else {
            toastError('Unable to find matching brand');
        }
    };

    const columns: GridColDef[] = [
        {
            field: "brand",
            headerName: "Profile",
            flex: 1.5,
            renderCell: (params) => (
                <div className="flex items-center gap-3">
                    <img
                        src={params.row.brandImage}
                        alt={params.row.brandName}
                        className="w-10 h-10 rounded-full object-cover"
                    />
                    <Body2>{params.row.brandName}</Body2>
                </div>
            ),
        },
        {
            field: "name",
            headerName: "Website Name",
            flex: 2,
            renderCell: (params) => (
                <div className="websiteNameOrgTabLabel flex flex-col gap-1">
                    <Body2>{params.row.name}</Body2>
                    <TagSymphony
                        className="websiteTypeTag"
                        title={`${WEBSITE_TYPE_INFO[params.row.type as WebsiteType].title} ${params.row.label ? `(${params.row.label})` : ''}`}
                        icon={WEBSITE_TYPE_INFO[params.row.type as WebsiteType].icon}
                    />
                    {/* <RowContainer>
                        <WebsiteTypeBadge
                        websiteType={params.row.type}
                        additionalLabel={params.row.label}
                        />
                    </RowContainer> */}
                </div>
            ),
        },
        {
            field: "createdAt",
            headerName: "Created",
            flex: 1,
            renderCell: (params) => (
                <Body2>{format(new Date(params.row.createdAt), 'MMM dd, yyyy')}</Body2>
            ),
        },
        {
            field: "urlAndActions",
            headerName: "Website URL & Actions",
            flex: 4,
            renderCell: (params) => (
                <Container
                    display='flex'
                    flexDirection='row'
                    justifyContent='flex-start'
                    alignItems='center'
                    gap="24px"
                    width='100%'
                >
                    <LinkContainer
                        url={params.row.url}
                        showShareButton={false}
                        width='80%'
                    />
                    <div className="flex gap-2">
                        <ButtonSymphony
                            className="editWebsiteButton"
                            variant="text"
                            onClick={() => handleRowClick(params.row)}
                        >
                            Edit site
                        </ButtonSymphony>
                    </div>
                </Container>
            ),
        },
    ];

    const handleCreateWebsite = () => {
        setIsBrandModalOpen(true);
    };

    const handleBrandSelect = (brandId: number) => {
        const selectedBrand = organizationBrands.find(brand => brand.id === brandId);
        if (selectedBrand) {
            setCurrentBrand(selectedBrand);
            setIsBrandModalOpen(false);
            setShowCreateNewWebsiteModal(true);
        }
    };

    if (isLoadingTab) {
        return <OrganizationTabLoading />
    }

    // NOTE: render()
    return (
        <>
            <OrganizationTabSection
                className="organizationPageWebsitesTab"
                buttonClassName="organizationPageCreateWebsiteButton"
                title="Websites"
                buttonText="Create a Website"
                onButtonClick={handleCreateWebsite}
                showDividers
            >
                <OrganizationViewDefaultGrid
                    className="organizationWebsitesGrid"
                    rows={websites}
                    columns={columns}
                    hideFooter={false}
                    disableColumnMenu
                    disableColumnSelector
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    itemsPerPage={PAGE_SIZE}
                    itemName="Websites"
                    rowCount={rowCount}
                    paginationMode="server"
                    autoHeight={true}
                    // isLoadingData={isLoading}
                    emptyStateComponent={
                        <TableEmptyState
                            className="organizationWebsitesTableEmptyState"
                            style={{ marginTop: 10 }}
                            primaryText="View and manage all of your team's websites and pre-save campaigns here."
                            buttonText="Create your First Website"
                            buttonAction={handleCreateWebsite}
                        />
                    }
                />
            </OrganizationTabSection>

            {/* NOTE: Modals */}
            <BrandSelectionModal
                className="websitesBrandSelectionModal"
                isOpen={isBrandModalOpen}
                onClose={() => setIsBrandModalOpen(false)}
                onBrandSelect={handleBrandSelect}
                titleText="Select a Brand"
                subtitleText="Choose the brand you want to create a website for."
            />
        </>
    );
};

export default OrganizationTabWebsites;